export type ProfileShareTab = 'works' | 'services' | 'briefs' | 'reviews' | 'likes' | 'favorites';

const getAppOrigin = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    return window.location.origin;
};

export const buildProfilePath = (userId: string) => `/profile/${encodeURIComponent(userId)}`;

export const buildOwnProfilePath = (userId: string) => `${buildProfilePath(userId)}/me`;

// A password-protected project share link. The recipient opens it, gets
// prompted for the password, and is granted collaborator access.
export const buildContractShareUrl = (contractId: string, shareToken: string) =>
    `${getAppOrigin()}/contracts/${encodeURIComponent(contractId)}?share=${encodeURIComponent(shareToken)}`;

const buildShareUrlWithParams = (userId: string, params?: Record<string, string>) => {
    const url = new URL(`${getAppOrigin()}${buildProfilePath(userId)}`);
    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (value) {
                url.searchParams.set(key, value);
            }
        });
    }
    return url.toString();
};

export const buildProfileShareUrl = (userId: string) => buildShareUrlWithParams(userId);

export const buildWorkSharePath = (userId: string, postId: string | number) => `${buildProfilePath(userId)}?tab=works&postId=${encodeURIComponent(String(postId))}`;

export const buildServiceSharePath = (userId: string, serviceId: string | number) => `${buildProfilePath(userId)}?tab=services&serviceId=${encodeURIComponent(String(serviceId))}`;

export const buildBriefSharePath = (userId: string, briefId: string | number) => `${buildProfilePath(userId)}?tab=briefs&briefId=${encodeURIComponent(String(briefId))}`;

export const buildWorkShareUrl = (userId: string, postId: string | number) => buildShareUrlWithParams(userId, {
    tab: 'works',
    postId: String(postId)
});

export const buildServiceShareUrl = (userId: string, serviceId: string | number) => buildShareUrlWithParams(userId, {
    tab: 'services',
    serviceId: String(serviceId)
});

export const buildBriefShareUrl = (userId: string, briefId: string | number) => buildShareUrlWithParams(userId, {
    tab: 'briefs',
    briefId: String(briefId)
});

export const buildTrialShareUrl = (trialId: string | number) => {
    return `${getAppOrigin()}/trials/${encodeURIComponent(String(trialId))}`;
};

export const buildProfileShareText = (_displayName?: string) => '我分享给你了一个云作用户';

export const buildWorkShareText = (_title?: string, _authorName?: string) => '我分享给你了一个云作作品';

export const buildServiceShareText = (_title?: string, _authorName?: string) => '我分享给你了一个云作服务';

export const buildBriefShareText = (_title?: string, _authorName?: string) => '我分享给你了一个云作需求';

export const buildTrialShareText = () => '我分享给你了一个云作试稿';

export const buildShareClipboardText = (shareText: string, url: string) => `${shareText}: ${url}`;

export const resolveProfileShareState = (searchParams: URLSearchParams) => {
    const postId = searchParams.get('postId') || undefined;
    const serviceId = searchParams.get('serviceId') || undefined;
    const briefId = searchParams.get('briefId') || undefined;
    const rawTab = searchParams.get('tab') as ProfileShareTab | null;

    let tab: ProfileShareTab | undefined;
    if (postId) {
        tab = 'works';
    } else if (serviceId) {
        tab = 'services';
    } else if (briefId) {
        tab = 'briefs';
    } else if (rawTab && ['works', 'services', 'briefs', 'reviews', 'likes', 'favorites'].includes(rawTab)) {
        tab = rawTab;
    }

    return {
        tab,
        postId,
        serviceId,
        briefId
    };
};

export const copyTextToClipboard = async (text: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
};
